import { MantineStyleProps } from '@mantine/core';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import {
  BaseFinancialItem,
  PensionAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { AssetDescriptionCell } from 'src/financial/financial-data/tables/AssetDescriptionCell';
import {
  FinancialItemGenericTable,
  formatDate,
  getFinancialItemType,
} from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

type AccountsTableProps = {
  financialItems: PensionAsset[];
  metadata: DynamicUiEntitiesMetadata;
  pl?: MantineStyleProps['pl'];
  pr?: MantineStyleProps['pr'];
  titleParts?: string[];
  onSubmit: () => void;
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export default function PensionsTable({
  financialItems,
  metadata,
  pl = 50,
  onRowClick,
}: AccountsTableProps) {
  const currency = useCurrency();
  const pensionProductToUserFriendlyName = (pension: PensionAsset) => {
    const pensionProduct = pension.pensionProduct;

    if (pensionProduct === 'DefinedBenefitPension') {
      return 'Defined benefit';
    }

    if (pensionProduct === 'DefinedContributionPension') {
      return 'Defined contribution';
    }

    if (pensionProduct === 'PensionAnnuity') {
      return 'Annuity';
    }

    if (pensionProduct === 'IncomeDrawdownPension') {
      return 'Income drawdown';
    }

    if (pensionProduct === 'StatePension') {
      return 'State Pension';
    }

    return pensionProduct satisfies never;
  };

  const fieldsRenderingRules = [
    {
      headerTitle: 'Description',
      sortable: true,
      sortKey: 'description',
      render: (item: PensionAsset) => (
        <AssetDescriptionCell
          description={item.description}
          isUpdatedAutomatically={item.isUpdatedAutomatically}
          canConnectToFeed={item.canConnectToFeed}
        />
      ),
    },
    {
      headerTitle: 'Pension product',
      sortable: true,
      sortKey: 'pensionProduct',
      render: (item: PensionAsset) => pensionProductToUserFriendlyName(item),
    },
    {
      headerTitle: 'Provider',
      sortable: true,
      sortKey: 'provider',
      render: (item: PensionAsset) => item.provider,
    },
    {
      headerTitle: 'Provider Reference Number',
      sortable: true,
      sortKey: 'providerReferenceNumber',
      render: (item: PensionAsset) => item.providerReferenceNumber,
    },
    {
      headerTitle: 'Type',
      sortable: true,
      sortKey: 'type',
      render: (item: PensionAsset) => getFinancialItemType(item, metadata),
    },
    {
      headerTitle: 'Last updated',
      sortable: true,
      sortKey: 'valueDate',
      render: (item: PensionAsset) => formatDate(item.valueDate),
    },
    {
      headerTitle: 'Current value',
      sortable: true,
      sortKey: 'value',
      render: (item: PensionAsset) =>
        item.value != null ? formatMoney(item.value, currency) : undefined,
    },
  ];

  return (
    <>
      <FinancialItemGenericTable
        financialItems={financialItems}
        fieldsRenderingRules={fieldsRenderingRules}
        pl={pl}
        onRowClick={onRowClick}
      />
    </>
  );
}
